import React, { useRef } from 'react'
import * as htmlToImage from 'html-to-image'
import { QRCodeSVG } from 'qrcode.react'
import { Button } from '@/components/ui/button'
import { Download, X, Users, Crown } from 'lucide-react'

// Card is 600×auto — no wasted whitespace, looks great on mobile shares
const CARD_WIDTH = 600

export default function DownloadCard({ post, votes, userVote, onClose, isPremium }) {
  const ref = useRef(null)

  const LABEL = { agree: 'AGREED', disagree: 'DISAGREED', neutral: 'NEUTRAL' }[userVote]
  const COLOR = { agree: '#22c55e', disagree: '#ef4444', neutral: '#a855f7' }[userVote]
  const COLOR_SOFT = { agree: 'rgba(34,197,94,0.15)', disagree: 'rgba(239,68,68,0.15)', neutral: 'rgba(168,85,247,0.15)' }[userVote]

  const download = async () => {
    try {
      const url = await htmlToImage.toPng(ref.current, {
        pixelRatio: 2,
        backgroundColor: '#020617',
        skipFonts: true,
        cacheBust: true,
      })
      const a = document.createElement('a')
      a.href = url
      a.download = `litx-verdict-${Date.now()}.png`
      a.click()
    } catch (err) {
      console.error('Download failed:', err)
      try {
        const url = await htmlToImage.toPng(ref.current, {
          pixelRatio: 2,
          backgroundColor: '#020617',
          skipFonts: true,
        })
        const a = document.createElement('a')
        a.href = url
        a.download = `litx-verdict-${Date.now()}.png`
        a.click()
      } catch {
        alert('Download failed. Please try again.')
      }
    }
  }

  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="relative flex flex-col" style={{ width: '100%', maxWidth: CARD_WIDTH, maxHeight: '95vh' }}>

        {/* Close */}
        <button
          onClick={onClose}
          className="absolute -top-3 -right-3 z-20 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition border border-white/10"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Scroll area */}
        <div className="flex-1 overflow-y-auto rounded-3xl">

          {/* ── EXPORT CARD ── all inline styles for html-to-image capture */}
          <div
            ref={ref}
            style={{
              width: '100%',
              borderRadius: 28,
              overflow: 'hidden',
              background: 'linear-gradient(145deg, #1e1b4b 0%, #0f172a 55%, #020617 100%)',
              color: '#fff',
              fontFamily: 'system-ui, -apple-system, Arial, sans-serif',
              padding: 40,
              boxSizing: 'border-box',
              position: 'relative',
            }}
          >
            {/* Glow orbs */}
            <div style={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.3) 0%, transparent 70%)', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', bottom: -30, left: -30, width: 150, height: 150, borderRadius: '50%', background: 'radial-gradient(circle, rgba(6,182,212,0.2) 0%, transparent 70%)', pointerEvents: 'none' }} />

            {/* Premium badge */}
            {isPremium && (
              <div style={{ position: 'absolute', top: 20, right: 20, display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 999, background: 'rgba(250,204,21,0.12)', border: '1px solid rgba(250,204,21,0.35)' }}>
                <Crown size={13} color="#facc15" />
                <span style={{ fontSize: 11, fontWeight: 700, color: '#fde047', letterSpacing: 1 }}>PREMIUM</span>
              </div>
            )}

            {/* Header row — LitX branding */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 32 }}>
              <div>
                <div style={{ fontSize: 32, fontWeight: 800, letterSpacing: -0.5, background: 'linear-gradient(135deg, #38bdf8, #818cf8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>LitX</div>
                <div style={{ opacity: 0.4, letterSpacing: 3, fontSize: 10, marginTop: 2, color: '#94a3b8' }}>ANONYMOUS VERDICT</div>
              </div>
              {/* Verdict badge in header */}
              <div style={{ display: 'inline-flex', alignItems: 'center', padding: '8px 18px', borderRadius: 999, background: COLOR, color: '#fff', fontWeight: 800, fontSize: 13, letterSpacing: 1 }}>
                {LABEL}
              </div>
            </div>

            {/* Content card */}
            <div style={{ background: 'rgba(255,255,255,0.055)', borderRadius: 20, padding: 32, marginBottom: 28, border: '1px solid rgba(255,255,255,0.08)', position: 'relative', overflow: 'hidden' }}>
              {/* Subtle color tint from vote */}
              <div style={{ position: 'absolute', inset: 0, background: COLOR_SOFT, borderRadius: 20, pointerEvents: 'none' }} />
              <div style={{ position: 'relative', fontSize: 22, lineHeight: 1.5, fontWeight: 700, color: '#f1f5f9', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                "{post.content}"
              </div>
              <div style={{ position: 'relative', marginTop: 16, opacity: 0.5, fontSize: 14, color: '#94a3b8' }}>
                — {post.author_username}
              </div>
            </div>

            {/* Stats row */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
              {[
                { label: 'Agree', pct: votes.agree_percent, color: '#22c55e' },
                { label: 'Disagree', pct: votes.disagree_percent, color: '#ef4444' },
                { label: 'Neutral', pct: votes.neutral_percent, color: '#a855f7' },
              ].map(({ label, pct, color }) => (
                <div key={label} style={{ flex: 1, minWidth: 100, background: 'rgba(255,255,255,0.05)', borderRadius: 12, padding: '10px 14px', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4, opacity: 0.7 }}>{label}</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color }}>{pct}%</div>
                  {/* Mini progress bar */}
                  <div style={{ marginTop: 6, height: 3, background: 'rgba(255,255,255,0.1)', borderRadius: 99, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 99 }} />
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {/* Votes pill */}
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.07)', padding: '6px 12px', borderRadius: 999, border: '1px solid rgba(255,255,255,0.08)' }}>
                  <Users size={13} color="#94a3b8" />
                  <strong style={{ color: '#f1f5f9', fontSize: 13 }}>{votes.total}</strong>
                  <span style={{ opacity: 0.5, fontSize: 12 }}>votes</span>
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#38bdf8' }}>litx.in</div>
                  <div style={{ fontSize: 9, letterSpacing: 2, opacity: 0.3, color: '#94a3b8' }}>VERDICT PLATFORM</div>
                </div>
              </div>

              {/* QR code — smaller, proportional */}
              <div style={{ background: '#fff', padding: 10, borderRadius: 12, flexShrink: 0 }}>
                <QRCodeSVG value="https://litx.in" size={72} />
              </div>
            </div>
          </div>
        </div>

        {/* Download button */}
        <div className="mt-4 flex justify-center">
          <Button
            onClick={download}
            className="px-8 py-5 text-sm bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white rounded-2xl shadow-lg font-semibold"
          >
            <Download className="mr-2 w-4 h-4" />
            Download Card
          </Button>
        </div>
      </div>
    </div>
  )
}